import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ChevronRight, LucideIcon, Repeat, Target, Timer } from 'lucide-react'
import { DateTime } from 'luxon'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import { icons } from '../../styles/images'
import { TTaskV4 } from '../../utils/types'
import { Icon } from '../atoms/Icon'
import { ConsolidationCard } from './creation/mirror/ConsolidationCard'
import { MirrorQuickAdd } from './creation/mirror/MirrorQuickAdd'
import { isLabMode, useGoalCreation } from './creation/shared/GoalCreationContext'
import { isDueToday, linkedTasksForGoal, nextUpOrder } from './goalTasks'
import {
    GOAL_STATUS_LABEL,
    TGoal,
    TGoalStatus,
    TGoalType,
    accomplishedDateLabel,
    accomplishedGoals,
    accomplishedReadout,
    activeGoals,
    archivedGoals,
    formatGoalAmount,
    isGoalAchieved,
    isGoalArchived,
    isLegacyFinishedGoal,
} from './goalTypes'
import { NO_GOAL_COPY, noGoalReason } from './noGoalNudgeRules'

/**
 * `isArchived` wins over the status because an archived goal's verdict is held, not current — it is
 * kept on the record so restoring reads the way it did before, but showing "On track" for a goal
 * that left the list would be a claim about work that is no longer being counted. Reuses the paused
 * token set: quiet, no new colour.
 */
export const GoalStatusBadge = ({
    status,
    isArchived,
    isFinished,
    isAccomplished,
}: {
    status: TGoalStatus
    isArchived?: boolean
    /** Archived by the weekly review's "Archive as done": it left the list finished, not abandoned. */
    isFinished?: boolean
    /**
     * Achieved, or a legacy "Archive as done": the one place an achievement keeps colour, in the
     * same success tokens "On track" uses. Wins over the other two.
     */
    isAccomplished?: boolean
}) => (
    // A span, not Badge's div: the badge sits inside the goal card, which is a button.
    <span
        className={cn(
            'inline-flex h-5 shrink-0 items-center rounded-sm px-2 text-label-md',
            isAccomplished && 'bg-success/10 text-success',
            !isAccomplished && isArchived && 'bg-secondary text-muted-foreground',
            !isAccomplished && !isArchived && status === 'on_track' && 'bg-success/10 text-success',
            !isAccomplished && !isArchived && status === 'behind' && 'bg-gt-yellow/20 text-gt-gold',
            !isAccomplished && !isArchived && status === 'off_track' && 'bg-destructive/[0.08] text-destructive',
            !isAccomplished && !isArchived && status === 'paused' && 'bg-secondary text-muted-foreground'
        )}
    >
        {isAccomplished
            ? 'Accomplished'
            : isArchived
            ? isFinished
                ? 'Finished'
                : 'Archived'
            : GOAL_STATUS_LABEL[status]}
    </span>
)

const TYPE_TILE: Record<TGoalType | 'none', { Icon: LucideIcon; tone: string }> = {
    consistency: { Icon: Repeat, tone: 'bg-gt-violet/10 text-gt-violet' },
    time: { Icon: Timer, tone: 'bg-info/10 text-info' },
    none: { Icon: Target, tone: 'bg-muted-foreground/10 text-secondary-foreground' },
}

/** The 28px tile that leads a goal card: repeat for consistency goals, timer for hours goals. */
const GoalTypeTile = ({ type }: { type?: TGoalType }) => {
    const { Icon: TileIcon, tone } = TYPE_TILE[type ?? 'none']
    return (
        <span className={cn('flex size-7 shrink-0 items-center justify-center rounded-md', tone)}>
            <TileIcon size={14} strokeWidth={2.25} aria-hidden />
        </span>
    )
}

// Past ten a row of dots stops being countable at a glance; the words carry it alone.
const MAX_DOTS = 10

/** This week against the weekly pace: one dot per session (or hour), filled as they land. */
const WeekCounter = ({ goal }: { goal: TGoal }) => {
    const target = goal.weeklyTarget ?? 0
    const done = goal.thisWeek ?? 0
    const unit = goal.unit === 'hrs' ? ' hrs' : ''
    const showDots = Number.isInteger(target) && target <= MAX_DOTS
    return (
        <span className="flex items-center gap-1.5">
            {showDots && (
                <span className="flex shrink-0 items-center gap-[3px]" aria-hidden>
                    {Array.from({ length: target }, (_, index) => (
                        <span
                            key={index}
                            className={cn(
                                'size-2 rounded-full',
                                index < Math.floor(done)
                                    ? goal.goalType === 'time'
                                        ? 'bg-info'
                                        : 'bg-gt-violet'
                                    : 'border border-border'
                            )}
                        />
                    ))}
                </span>
            )}
            <span className="truncate text-body-sm text-secondary-foreground">
                {formatGoalAmount(done)} of {formatGoalAmount(target)}
                {unit} this week
            </span>
        </span>
    )
}

/** Today, or a past due date in warning; undated tasks read without one. */
const NextStep = ({ task }: { task: TTaskV4 }) => {
    const due = task.due_date ? DateTime.fromISO(task.due_date) : null
    // A cleared due date comes back as the epoch; treat it like no date.
    const validDue = due?.isValid && +due !== 0 ? due : null
    const isLate = !!validDue && !isDueToday(task) && validDue < DateTime.local().startOf('day')
    return (
        <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-body-sm">
            <span className="font-medium text-muted-foreground">Next</span>
            <span className="truncate text-secondary-foreground">
                {task.title}
                {validDue && (
                    <span className={isLate ? 'text-warning' : 'text-muted-foreground'}>
                        {' · '}
                        {isDueToday(task) ? 'today' : `${isLate ? 'due ' : ''}${validDue.toFormat('LLL d')}`}
                    </span>
                )}
            </span>
        </span>
    )
}

interface GoalCardProps {
    goal: TGoal
    isSelected: boolean
    onClick: () => void
    /** The goal's first open linked task, in Next up order. */
    nextTask?: TTaskV4
    /** During a weekly review: what the owner said about this goal, as one line under the readout. */
    reviewLine?: string
}
/** The accomplished card's second row, in the slot and type the Next row uses. */
const AccomplishedDate = ({ goal }: { goal: TGoal }) => {
    const [prefix, ...rest] = accomplishedDateLabel(goal).split(' ')
    return (
        <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-body-sm">
            <span className="font-medium text-muted-foreground">{prefix}</span>
            <span className="truncate text-secondary-foreground">{rest.join(' ')}</span>
        </span>
    )
}

/**
 * Card in the goals grid: type tile, then title and status, this week against the pace, and the
 * next linked task. An accomplished goal uses the same card with its final readout and the date it
 * was achieved in those two rows, so the two grids share one rhythm.
 *
 * A real button, so the grid is reachable by keyboard and the selected card is announced: during a
 * weekly review these cards are the queue, and a queue you cannot tab through is not one. The
 * children are spans because a button may only contain phrasing content.
 */
const GoalCard = ({ goal, isSelected, onClick, nextTask, reviewLine }: GoalCardProps) => {
    const isPaced = !!goal.weeklyTarget && goal.progressMode !== 'manual'
    const isAccomplished = isGoalAchieved(goal) || isLegacyFinishedGoal(goal)
    const isPending = Boolean(goal.optimisticId)
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isPending}
            aria-current={isSelected ? 'true' : undefined}
            data-goal-card={goal.id}
            className={cn(
                'flex w-full cursor-pointer items-start gap-2.5 rounded-gt-md border p-3 text-left transition-colors duration-fast ease-out',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                'disabled:cursor-wait disabled:opacity-70',
                isSelected ? 'border-border bg-secondary' : 'border-hairline bg-background hover:bg-card'
            )}
        >
            <GoalTypeTile type={goal.goalType} />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="flex w-full items-center gap-2">
                    <span className="min-w-0 flex-1 text-title-sm text-foreground">{goal.title}</span>
                    <GoalStatusBadge
                        status={goal.status}
                        isArchived={isGoalArchived(goal)}
                        isFinished={goal.archivedReason === 'done'}
                        isAccomplished={isAccomplished}
                    />
                </span>
                {isAccomplished ? (
                    <span className="truncate text-body-sm text-secondary-foreground">{accomplishedReadout(goal)}</span>
                ) : isPaced ? (
                    <WeekCounter goal={goal} />
                ) : (
                    <span className="truncate text-body-sm text-secondary-foreground">
                        {goal.progressLabel}
                        {goal.progressMode === 'manual' && (
                            <span className="text-muted-foreground"> · self-graded</span>
                        )}
                    </span>
                )}
                {isAccomplished ? <AccomplishedDate goal={goal} /> : nextTask && <NextStep task={nextTask} />}
                {reviewLine && (
                    <span
                        className="mt-1 truncate border-t border-hairline pt-2 text-body-sm text-secondary-foreground"
                        data-testid="goal-review-line"
                    >
                        {reviewLine}
                    </span>
                )}
            </span>
        </button>
    )
}

interface GoalsListProps {
    selectedGoalId: string | null
    onSelect: (id: string) => void
    // The dedicated Goals page renders its own page header + actions; the
    // embedded section header is only for surfaces that lack one.
    hideSectionHeader?: boolean
    /** Include archived goals, so they can be found and restored. Off everywhere by default. */
    showArchived?: boolean
    /** Goal id → the line its card shows while a weekly review is open. */
    reviewLines?: Record<string, string>
}
const GoalsList = ({ selectedGoalId, onSelect, hideSectionHeader, showArchived, reviewLines }: GoalsListProps) => {
    const {
        allGoals,
        areGoalsLoading,
        areGoalsUnavailable,
        openFlow,
        iteration,
        createdGoals,
        consolidationDismissed,
        mirrorGoalId,
        taskGoalLinks,
        justAchievedGoalId,
        clearJustAchievedGoalId,
    } = useGoalCreation()
    const { data: tasks } = useGetTasksV4()

    // The Mirror's created goal is lab-only and tracked by id; its presence gates
    // the consolidation card off and the quick-add on.
    const isMirror = isLabMode && iteration === 'mirror'
    const mirrorGoal = isMirror && mirrorGoalId ? createdGoals.find((g) => g.id === mirrorGoalId) : undefined
    const showConsolidation = isMirror && !mirrorGoal && !consolidationDismissed
    const showQuickAdd = isMirror && mirrorGoal != null
    // Archived goals are fetched with the rest but stay out of the list until asked for: an archived
    // goal is not a goal you are working on, and the toggle above is the only way back to it.
    const visibleGoals = showArchived ? [...activeGoals(allGoals), ...archivedGoals(allGoals)] : activeGoals(allGoals)
    // Finished goals sit below the active grid behind a disclosure: collapsed by default, opened once
    // for a goal just achieved so the card is seen landing there.
    const accomplished = accomplishedGoals(allGoals)
    const [isAccomplishedOpen, setIsAccomplishedOpen] = useState(false)
    const [announcement, setAnnouncement] = useState('')
    const justAchievedIsListed = !!justAchievedGoalId && accomplished.some((goal) => goal.id === justAchievedGoalId)
    useEffect(() => {
        if (!justAchievedGoalId || !justAchievedIsListed) return
        setIsAccomplishedOpen(true)
        setAnnouncement('Goal achieved. Moved to Accomplished.')
        // After the section has rendered open: move focus to the card that moved, and bring it into
        // view. Cleared only then, so the re-render the clear causes cannot cancel the reveal.
        requestAnimationFrame(() => {
            const card = document.querySelector<HTMLElement>(`[data-goal-card="${justAchievedGoalId}"]`)
            card?.focus({ preventScroll: true })
            card?.scrollIntoView({ block: 'nearest' })
            clearJustAchievedGoalId()
        })
    }, [justAchievedGoalId, justAchievedIsListed, clearJustAchievedGoalId])
    // Same rule and copy as the overview nudge, so the two surfaces agree — and so this card stops
    // flashing before the first goals response lands. noGoalReason discounts archived goals itself.
    const showEmptyState =
        !showConsolidation && noGoalReason({ goals: allGoals, areGoalsLoading, areGoalsUnavailable }) === 'none'

    return (
        <div className="mb-4">
            {!hideSectionHeader && (
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-label-md uppercase tracking-normal text-muted-foreground">Goals</span>
                    {!isMirror && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1.5 text-muted-foreground hover:bg-secondary"
                            onClick={openFlow}
                        >
                            <Icon icon={icons.plus} color="gray" size="small" />
                            New goal
                        </Button>
                    )}
                </div>
            )}
            {showConsolidation && <ConsolidationCard />}
            {showEmptyState && (
                <div className="flex flex-col items-center gap-1 rounded-gt-md border border-dashed border-border bg-card px-4 py-6 text-center animate-in fade-in-0 duration-300">
                    <p className="text-title-sm text-foreground">
                        {accomplished.length > 0 ? 'No active goals' : NO_GOAL_COPY.none.title}
                    </p>
                    <p className="max-w-[320px] text-body-sm text-muted-foreground">{NO_GOAL_COPY.none.body}</p>
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 bg-action text-action-foreground transition-transform hover:bg-action-hover active:scale-[0.96]"
                        onClick={openFlow}
                    >
                        + New goal
                    </Button>
                </div>
            )}
            {/* Responsive grid; cards stretch to fill the pane so no dead space sits to their right. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2">
                {visibleGoals.map((goal) => (
                    <GoalCard
                        key={goal.id}
                        goal={goal}
                        isSelected={goal.id === selectedGoalId}
                        onClick={() => onSelect(goal.id)}
                        nextTask={nextUpOrder(linkedTasksForGoal(goal.id, tasks, taskGoalLinks))[0]}
                        reviewLine={reviewLines?.[goal.id]}
                    />
                ))}
            </div>
            {accomplished.length > 0 && (
                <div className="mt-4">
                    <button
                        type="button"
                        aria-expanded={isAccomplishedOpen}
                        aria-controls="accomplished-goals"
                        onClick={() => setIsAccomplishedOpen((open) => !open)}
                        className="flex min-h-11 w-full items-center gap-1.5 border-t border-hairline px-1 text-left text-label-md uppercase tracking-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:min-h-9"
                    >
                        <ChevronRight
                            size={14}
                            aria-hidden
                            className={cn('transition-transform duration-fast', isAccomplishedOpen && 'rotate-90')}
                        />
                        Accomplished
                        <span className="tabular-nums text-muted-foreground/70">{accomplished.length}</span>
                    </button>
                    {isAccomplishedOpen && (
                        <div
                            id="accomplished-goals"
                            className="mt-1 grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2"
                        >
                            {accomplished.map((goal) => (
                                <GoalCard
                                    key={goal.id}
                                    goal={goal}
                                    isSelected={goal.id === selectedGoalId}
                                    onClick={() => onSelect(goal.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
            <span className="sr-only" aria-live="polite">
                {announcement}
            </span>
            {showQuickAdd && mirrorGoal && <MirrorQuickAdd goalId={mirrorGoal.id} />}
        </div>
    )
}

export default GoalsList
